import { PageQuery, Paginate } from '@business/entities/common';
import {
  OrderBrandId,
  OrderProductId,
  OrderProductPricingResponse,
  OrderProductResponse,
  ProviderPricesResponse,
  SearchProductResponse,
} from '@business/entities/order';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { PageStatResponse } from '@business/entities/stats';
import { CategoryId, MasterCategoryResponse, MasterProductResponse } from '@business/entities/category';
import { MasterListFilterData } from '@modules/orders/MasterProductsPage/MasterProductsPage';
import { ProductFormData } from '@modules/orders/ProductsPage/AddEditProductOverlay/OrderProductForm';
import { ManageNonOptionalPricingData } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/CategoryTab/ManageNonOptionalPricingOverlay/ManageNonOptionalPricingOverlay';

export function fetchProductsPaginated(
  params: PageQuery,
  products?: OrderProductId[],
  vendorId?: string,
  categoryId?: string,
  brandId?: OrderBrandId,
  status?: string,
  fromAddProduct = false,
) {
  return Endpoint.get<Paginate<OrderProductResponse>>(`@gc/products`, {
    params: R.objectCompact({ ...params, ...R.objectCompact({ products, vendorId, categoryId, brandId, status }), fromAddProduct }),
  });
}

export function fetchProducts(isPaginated?: string) {
  const params = R.objectCompact({ isPaginated });
  return Endpoint.get<OrderProductResponse[]>(`@gc/products`, { params });
}

export function fetchAllProducts() {
  return Endpoint.get<OrderProductResponse[]>(`@gc/products/all`);
}

export function fetchProduct(id: OrderProductId) {
  return Endpoint.get<OrderProductResponse>(`@gc/products/product/${id}`);
}

export function createProduct(data: Partial<ProductFormData>) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<OrderProductResponse>(`@gc/products`, { formData, hasFiles: true });
}
export function duplicateProduct(id: OrderProductId) {
  return Endpoint.post<OrderProductResponse>(`@gc/duplicate/Product/${id}`);
}

export function updateProduct(id: OrderProductId, data: Partial<ProductFormData>) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<OrderProductResponse>(`@gc/products/product/${id}`, { formData, hasFiles: true });
}

export function deleteProduct(id: OrderProductId) {
  return Endpoint.delete<void>(`@gc/products/${id}`);
}

export function toggleStatus(id: OrderProductId) {
  return Endpoint.post<OrderProductResponse>(`@gc/products/status/${id}`);
}

export function syncProducts() {
  return Endpoint.get<OrderProductResponse[]>(`@gc/products/sync`);
}

export function pageStats() {
  return Endpoint.get<PageStatResponse[]>(`@gc/products/page-stats`);
}

export function searchProducts(search: string, categoryId?: CategoryId, options?: any) {
  const params = R.objectCompact({ search, categoryId, ...options });
  return Endpoint.get<Paginate<SearchProductResponse>>(`@gc/products`, { params });
}

export function fetchProductsByCategory(categoryId: CategoryId, filters?: MasterListFilterData | { onlyPricing?: boolean }) {
  const params = R.objectCompact(filters);
  return Endpoint.get<(OrderProductResponse | OrderProductPricingResponse)[]>(`@gc/products/products-by-category/${categoryId}`, { params });
}

export function sortProducts(categoryId: CategoryId, productIds: OrderProductId[]) {
  return Endpoint.post<void>(`@gc/products/sort/${categoryId}`, { data: { productIds } });
}

export function fetchMasterCategories(filters?: MasterListFilterData) {
  const params = R.objectCompact(filters);
  return Endpoint.get<MasterCategoryResponse[]>(`@gc/products/master-categories`, { params });
}

export function updateOptionItemPrice(productId: OrderProductId, providers: ProviderPricesResponse[]) {
  return Endpoint.patch<OrderProductPricingResponse>(`@gc/products/product-pricing/${productId}`, { data: { providers } });
}

export function updateNonOptionalPricing(data: ManageNonOptionalPricingData) {
  return Endpoint.post<OrderProductPricingResponse>(`@gc/products/non-product-pricing`, { data });
}
